import React from "react";
import * as Mui from "@mui/material";
import { SettingsSection } from "component/shared/settings-page";
import { DescribedRadioGroup } from "component/shared/described-radio-group";
import type { DescribedRadioOption } from "component/shared/described-radio-group";
import { KestrelVersion } from "type/kestrel-version";

interface Props {
	value: KestrelVersion;
	onChange: (value: KestrelVersion) => void;
	kestrelAll: boolean;
	/** A move up to Version 10 that will need setting up once saved (MAX-4577). */
	upgradeNeedsSetup?: boolean;
}

// Version cards (VPLAT 1, 31). Version 10 is only offered to clients permitted it, and the old
// "Detail Pages Only" option is dropped unless it is the client's current value, so saving never
// silently moves anyone off it.
export const VersionSection: React.FC<Props> = ({ value, onChange, kestrelAll, upgradeNeedsSetup }) => {
	const options: DescribedRadioOption[] = [];
	if (kestrelAll) {
		options.push({
			value: KestrelVersion.ALL.id,
			label: "Version 10",
			tag: { label: "Recommended" },
			description: "Modern styling across pages and widgets, with a rebuilt search experience featuring advanced filters, embedded virtual tours, and interactive maps.",
			// Under the option it is about, not the whole group (MAX-4577).
			after: upgradeNeedsSetup && (
				<Mui.Alert severity="info" role="status">
					Version 10 requires additional setup. Once you save, you'll find it below.
				</Mui.Alert>
			),
		});
	}
	if (value.detail) {
		options.push({
			value: KestrelVersion.DETAIL.id,
			label: KestrelVersion.DETAIL.label,
		});
	}
	options.push({
		value: KestrelVersion.OFF.id,
		label: "Version 9",
		description: "The original version of iHomefinder. We recommend upgrading to Version 10 for a more modern search experience and the latest features.",
	});
	return (
		<SettingsSection refreshed title="Version" titleId="idx-version-title">
			<DescribedRadioGroup
				name="idx-version"
				aria-labelledby="idx-version-title"
				value={value.id}
				options={options}
				onChange={(id: string | number) => onChange(KestrelVersion.getByIdOrDefault(Number(id)))}
			/>
		</SettingsSection>
	);
};
